import { useEffect, useMemo, useState } from "react";
import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";

import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import PageGuide from "components/admin/PageGuide";
import Footer from "examples/Footer";
import PageCard from "components/admin/PageCard";
import { useConfirm } from "components/admin/Feedback";
import { diffLines } from "components/admin/confirmChanges";
import { useSearchParams } from "react-router-dom";
import { useFetch, useRunner } from "components/admin/hooks";
import api from "api/client";
import { useCan } from "auth/AuthContext";
import { PERMISSIONS } from "auth/access";
import ArrayTables from "layouts/balance/ArrayTables";

/** Màu viền của nhóm, chỉ để dễ phân biệt khi cuộn. */
const GROUP_COLOR = {
  "Chống spam bạn bè": "error",
  "Giảm thủ": "info",
  "Sàn PvE": "warning",
  PvP: "error",
  "Chí mạng": "primary",
  "Chí mạng quái": "warning",
  "Né / Chính xác": "info",
  "Điểm tiềm năng": "success",
  "Rơi đồ": "success",
};

function fmt(value, isInt) {
  if (value === null || value === undefined) return "";
  return isInt ? String(Math.round(value)) : String(Number(value.toFixed(4)));
}

/** Bỏ dấu để tìm "chi mang" khớp "Chí mạng". */
const plain = (t) =>
  String(t || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d")
    .toLowerCase();

function Balance() {
  // Tab ghi lên URL (?tab=arrays) để gửi link thẳng.
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "arrays" ? 1 : 0;
  const setTab = (v) => setParams(v === 1 ? { tab: "arrays" } : {}, { replace: true });
  const [query, setQuery] = useState("");
  const confirm = useConfirm();
  const [run, busy] = useRunner();
  const groups = useFetch(() => api.get("/api/admin/balance"), []);
  const canEdit = useCan(PERMISSIONS.BALANCE_EDIT);
  // Giá trị đang gõ trên ô nhập, tách khỏi dữ liệu máy chủ để biết cái nào đã đổi.
  const [draft, setDraft] = useState({});

  useEffect(() => {
    if (!groups.data) return;
    const next = {};
    groups.data.forEach((g) => g.items.forEach((it) => (next[it.key] = fmt(it.value, it.isInt))));
    setDraft(next);
  }, [groups.data]);

  const index = useMemo(() => {
    const map = {};
    (groups.data || []).forEach((g) => g.items.forEach((it) => (map[it.key] = it)));
    return map;
  }, [groups.data]);

  const dirty = useMemo(
    () =>
      Object.keys(draft).filter((key) => {
        const it = index[key];
        if (!it) return false;
        const typed = Number(draft[key]);
        if (draft[key] === "" || Number.isNaN(typed)) return false;
        return Math.abs(typed - it.value) > 0.0001;
      }),
    [draft, index]
  );

  const invalid = useMemo(
    () =>
      Object.keys(draft).filter((key) => {
        const it = index[key];
        if (!it) return false;
        const typed = Number(draft[key]);
        return draft[key] === "" || Number.isNaN(typed) || typed < it.min || typed > it.max;
      }),
    [draft, index]
  );

  // Rời trang khi còn tham số chưa lưu thì trình duyệt hỏi lại.
  useEffect(() => {
    if (dirty.length === 0) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty.length]);

  const save = async () => {
    const before = Object.fromEntries(dirty.map((key) => [key, index[key].value]));
    const after = Object.fromEntries(dirty.map((key) => [key, Number(draft[key])]));
    const { lines, jumps } = diffLines(
      before,
      after,
      dirty.map((key) => [key, index[key].label])
    );
    const ok = await confirm({
      title: `Lưu ${dirty.length} thay đổi`,
      message: [
        ...lines,
        "",
        jumps.length
          ? `⚠ ${jumps.join(", ")} thay đổi từ 2 lần trở lên — kiểm tra lại có gõ nhầm không.`
          : null,
        "Áp dụng ngay cho trận đánh mới, không cần khởi động lại máy chủ.",
      ]
        .filter((l) => l !== null)
        .join("\n"),
      color: jumps.length ? "error" : "warning",
      confirmText: "Lưu",
    });
    if (!ok) return;
    const res = await run(
      () =>
        api.put("/api/admin/balance/batch", {
          items: dirty.map((key) => ({ key, value: Number(draft[key]) })),
        }),
      (data) => `Đã đổi ${data.changed} tham số`
    );
    if (res.ok) groups.reload();
  };

  const resetOne = async (item) => {
    const ok = await confirm({
      title: "Khôi phục mặc định",
      message: `${item.label}: ${item.value} → ${item.defaultValue}`,
      confirmText: "Khôi phục",
    });
    if (!ok) return;
    const res = await run(
      () => api.post("/api/admin/balance/reset", { key: item.key }),
      "Đã khôi phục"
    );
    if (res.ok) groups.reload();
  };

  const revertDraft = () => {
    const next = {};
    (groups.data || []).forEach((g) =>
      g.items.forEach((it) => (next[it.key] = fmt(it.value, it.isInt)))
    );
    setDraft(next);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <PageGuide page="balance" />
        <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2, maxWidth: 420 }}>
          <Tab label="Tham số" />
          <Tab label="Bảng theo cấp" />
        </Tabs>
        {tab === 1 && <ArrayTables />}
        {tab === 0 && (
          <MDBox>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Các con số ở đây quyết định độ khó và phần thưởng của mọi người chơi. Chỉ sửa khi
              người cân bằng game yêu cầu. Bấm <strong>Lưu</strong> là áp dụng ngay cho trận đánh
              mới và giữ qua lần khởi động máy chủ sau.
            </Alert>
            <MDInput
              size="small"
              label="Tìm tham số (gõ tên, không cần dấu)"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              sx={{ mb: 2, minWidth: 300 }}
            />

            <MDBox
              display={canEdit ? "flex" : "none"}
              gap={2}
              alignItems="center"
              mb={1}
              flexWrap="wrap"
            >
              <MDButton
                color="info"
                disabled={busy || dirty.length === 0 || invalid.length > 0}
                onClick={save}
              >
                <Icon sx={{ mr: 1 }}>save</Icon>
                Lưu {dirty.length > 0 ? `(${dirty.length})` : ""}
              </MDButton>
              <MDButton
                color="secondary"
                variant="outlined"
                disabled={busy || dirty.length === 0}
                onClick={revertDraft}
              >
                Huỷ sửa
              </MDButton>
              {invalid.length > 0 && (
                <MDTypography variant="button" color="error">
                  {invalid.length} ô ngoài khoảng cho phép
                </MDTypography>
              )}
            </MDBox>

            {groups.error && <Alert severity="error">{groups.error}</Alert>}
            {groups.loading && <MDTypography variant="button">Đang tải…</MDTypography>}

            <Grid container spacing={3}>
              {(groups.data || [])
                .map((group) => ({
                  ...group,
                  items: group.items.filter(
                    (it) =>
                      !query.trim() ||
                      plain(`${it.label} ${it.key} ${it.doc || ""} ${group.group}`).includes(
                        plain(query.trim())
                      )
                  ),
                }))
                .filter((group) => group.items.length > 0)
                .map((group) => (
                  <Grid item xs={12} lg={6} key={group.group}>
                    <PageCard title={group.group} color={GROUP_COLOR[group.group] || "dark"}>
                      <MDBox display="flex" flexDirection="column" gap={2} pt={1}>
                        {group.items.map((item) => {
                          const typed = Number(draft[item.key]);
                          const bad =
                            draft[item.key] === "" ||
                            Number.isNaN(typed) ||
                            typed < item.min ||
                            typed > item.max;
                          const isDirty = !bad && Math.abs(typed - item.value) > 0.0001;
                          return (
                            <MDBox key={item.key} display="flex" alignItems="flex-start" gap={1.5}>
                              <MDBox flex={1} minWidth={0}>
                                <MDTypography variant="button" fontWeight="medium">
                                  {item.label}
                                  {item.unit ? ` (${item.unit})` : ""}
                                </MDTypography>
                                <Tooltip
                                  title={`Mã kỹ thuật: ${item.key}`}
                                  placement="bottom-start"
                                >
                                  <MDTypography variant="caption" color="text" display="block">
                                    Cho phép {item.min} – {item.max} · mặc định{" "}
                                    {fmt(item.defaultValue, item.isInt)}
                                    {isDirty && ` · đang là ${fmt(item.value, item.isInt)}`}
                                  </MDTypography>
                                </Tooltip>
                                {item.doc && (
                                  <MDTypography variant="caption" color="text" display="block">
                                    {item.doc}
                                  </MDTypography>
                                )}
                              </MDBox>
                              <MDInput
                                type="number"
                                value={draft[item.key] ?? ""}
                                error={bad}
                                success={isDirty}
                                inputProps={{
                                  step: item.isInt ? 1 : "any",
                                  min: item.min,
                                  max: item.max,
                                }}
                                sx={{ width: 120 }}
                                InputProps={{ readOnly: !canEdit }}
                                onChange={(e) => setDraft({ ...draft, [item.key]: e.target.value })}
                              />
                              {canEdit && (
                                <Tooltip
                                  title={item.changed ? "Khôi phục mặc định" : "Đang là mặc định"}
                                >
                                  <span>
                                    <MDButton
                                      iconOnly
                                      color="secondary"
                                      variant="text"
                                      disabled={busy || !item.changed}
                                      onClick={() => resetOne(item)}
                                    >
                                      <Icon>restart_alt</Icon>
                                    </MDButton>
                                  </span>
                                </Tooltip>
                              )}
                            </MDBox>
                          );
                        })}
                      </MDBox>
                    </PageCard>
                  </Grid>
                ))}
            </Grid>
          </MDBox>
        )}
        {tab === 0 && canEdit && dirty.length > 0 && (
          <MDBox
            position="sticky"
            bottom={16}
            mt={3}
            p={2}
            borderRadius="lg"
            display="flex"
            gap={2}
            alignItems="center"
            flexWrap="wrap"
            shadow="lg"
            sx={({ palette }) => ({
              backgroundColor: palette.background.card || palette.white.main,
              zIndex: 2,
            })}
          >
            <MDTypography variant="button" fontWeight="medium" flexGrow={1}>
              {dirty.length} tham số đã sửa, chưa lưu
              {invalid.length > 0 ? ` · ${invalid.length} ô ngoài khoảng cho phép` : ""}
            </MDTypography>
            <MDButton color="secondary" variant="outlined" disabled={busy} onClick={revertDraft}>
              Huỷ sửa
            </MDButton>
            <MDButton color="info" disabled={busy || invalid.length > 0} onClick={save}>
              <Icon sx={{ mr: 1 }}>save</Icon>Lưu ({dirty.length})…
            </MDButton>
          </MDBox>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Balance;
